/**
 * RTB Kiosk State Management
 * Handles device fingerprinting, persistent kiosk_mode token,
 * and contextual routing logic for Terminal Mode vs Staff Portal.
 *
 * The kiosk_mode key in LocalStorage is INTENTIONALLY non-expiring.
 * Only an explicit admin "Deregister" action removes it.
 */

import { create } from 'zustand';

// ─────────────────────────────────────────────
// Device Fingerprint Generator
// Combines stable, hardware-level signals to
// create a pseudo-unique device identifier that
// survives browser refreshes (unlike session IDs).
// ─────────────────────────────────────────────
export function generateDeviceFingerprint() {
  const signals = [
    navigator.userAgent,
    navigator.language,
    navigator.hardwareConcurrency || 'unknown',
    navigator.platform || 'unknown',
    `${screen.width}x${screen.height}x${screen.colorDepth}`,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    navigator.vendor || 'unknown',
  ];

  // Simple djb2-style hash over the concatenated string
  const raw = signals.join('||');
  let hash = 5381;
  for (let i = 0; i < raw.length; i++) {
    hash = (hash * 33) ^ raw.charCodeAt(i);
  }
  // Force unsigned 32-bit then convert to hex
  return (hash >>> 0).toString(16).padStart(8, '0');
}

// ─────────────────────────────────────────────
// LocalStorage keys (never auto-expire)
// ─────────────────────────────────────────────
export const KIOSK_STORAGE_KEYS = {
  MODE:       'rtb_kiosk_mode',      // 'true' | null
  DEVICE_ID:  'rtb_kiosk_device_id', // hex fingerprint
  VENUE_ID:   'rtb_kiosk_venue_id',  // UUID of registered venue
  VENUE_NAME: 'rtb_kiosk_venue_name',
  REGISTERED_AT: 'rtb_kiosk_registered_at',
  REGISTERED_BY: 'rtb_kiosk_registered_by', // email of admin who registered
};

// Roles that get full CRUD on the kiosk
export const MANAGER_ROLES = new Set(['super_admin', 'ops', 'gm', 'manager']);

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
export function isKioskDevice() {
  return localStorage.getItem(KIOSK_STORAGE_KEYS.MODE) === 'true';
}

export function getKioskVenueId() {
  return localStorage.getItem(KIOSK_STORAGE_KEYS.VENUE_ID);
}

export function getKioskMeta() {
  return {
    deviceId:      localStorage.getItem(KIOSK_STORAGE_KEYS.DEVICE_ID),
    venueId:       localStorage.getItem(KIOSK_STORAGE_KEYS.VENUE_ID),
    venueName:     localStorage.getItem(KIOSK_STORAGE_KEYS.VENUE_NAME),
    registeredAt:  localStorage.getItem(KIOSK_STORAGE_KEYS.REGISTERED_AT),
    registeredBy:  localStorage.getItem(KIOSK_STORAGE_KEYS.REGISTERED_BY),
  };
}

/**
 * registerAsKiosk – called by a manager/admin from the Settings or Users page.
 * Writes all kiosk flags to LocalStorage so they survive page reloads.
 * Returns the generated device fingerprint.
 */
export function registerAsKiosk({ venueId, venueName, registeredBy }) {
  const fingerprint = generateDeviceFingerprint();
  localStorage.setItem(KIOSK_STORAGE_KEYS.MODE, 'true');
  localStorage.setItem(KIOSK_STORAGE_KEYS.DEVICE_ID, fingerprint);
  localStorage.setItem(KIOSK_STORAGE_KEYS.VENUE_ID, venueId || '');
  localStorage.setItem(KIOSK_STORAGE_KEYS.VENUE_NAME, venueName || '');
  localStorage.setItem(KIOSK_STORAGE_KEYS.REGISTERED_AT, new Date().toISOString());
  localStorage.setItem(KIOSK_STORAGE_KEYS.REGISTERED_BY, registeredBy || '');
  return fingerprint;
}

/**
 * deregisterKiosk – wipes all kiosk keys.
 * Requires the calling component to have already confirmed admin auth.
 */
export function deregisterKiosk() {
  Object.values(KIOSK_STORAGE_KEYS).forEach((k) => localStorage.removeItem(k));
}

// ─────────────────────────────────────────────
// Zustand Store – reactive kiosk session state
// Tracks the CURRENT PIN SESSION (not the device token).
// Pin sessions expire on inactivity; the kiosk_mode token does not.
// ─────────────────────────────────────────────
export const useKioskStore = create((set, get) => ({
  // Device-level (persistent)
  isKioskDevice: isKioskDevice(),
  deviceId: localStorage.getItem(KIOSK_STORAGE_KEYS.DEVICE_ID),
  kioskVenueId: getKioskVenueId(),
  kioskVenueName: localStorage.getItem(KIOSK_STORAGE_KEYS.VENUE_NAME),

  // Session-level (ephemeral – cleared on inactivity timeout)
  pinSessionActive: false,
  pinSessionUserId: null,
  pinSessionUserName: null,
  pinSessionRole: null,

  // ── Device registration ──
  register: ({ venueId, venueName, registeredBy }) => {
    const fingerprint = registerAsKiosk({ venueId, venueName, registeredBy });
    set({
      isKioskDevice: true,
      deviceId: fingerprint,
      kioskVenueId: venueId,
      kioskVenueName: venueName,
    });
    return fingerprint;
  },

  deregister: () => {
    deregisterKiosk();
    set({
      isKioskDevice: false,
      deviceId: null,
      kioskVenueId: null,
      kioskVenueName: null,
      pinSessionActive: false,
      pinSessionUserId: null,
      pinSessionUserName: null,
      pinSessionRole: null,
    });
  },

  // ── PIN Session ──
  startPinSession: ({ userId, userName, role }) => {
    set({
      pinSessionActive: true,
      pinSessionUserId: userId,
      pinSessionUserName: userName,
      pinSessionRole: role,
    });
  },

  endPinSession: () => {
    set({
      pinSessionActive: false,
      pinSessionUserId: null,
      pinSessionUserName: null,
      pinSessionRole: null,
    });
  },
}));
